import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import type { CalendarBookingSearchResult } from '../../lib/calendarBookingSearch';
import { getBookingStatusBadgeClass } from '../../lib/bookingStatusStyles';

function formatBookingDate(row: CalendarBookingSearchResult): string {
  if (!row.start_date) return '';
  try {
    const d = parseISO(row.start_date);
    const datePart = format(d, 'MMM d, yyyy');
    return row.start_time ? `${datePart} · ${row.start_time.slice(0, 5)}` : datePart;
  } catch {
    return row.start_date;
  }
}

interface CalendarBookingSearchProps {
  query: string;
  results: CalendarBookingSearchResult[];
  loading: boolean;
  error: string | null;
  onSelectBooking: (bookingId: string) => void;
  /** Positioning for the results panel (e.g. mobile header search). */
  panelClassName?: string;
}

export default function CalendarBookingSearch({
  query,
  results,
  loading,
  error,
  onSelectBooking,
  panelClassName = 'left-0 right-0',
}: CalendarBookingSearchProps) {
  const trimmed = query.trim();
  if (trimmed.length < 2) return null;

  return (
    <div
      className={`absolute top-full z-30 mt-1 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden ${panelClassName}`}
      role="listbox"
      aria-label="Booking search results"
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          Searching…
        </div>
      ) : error ? (
        <p className="px-4 py-3 text-sm text-red-600">{error}</p>
      ) : results.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">No bookings found</p>
      ) : (
        <ul className="max-h-[min(320px,40vh)] overflow-y-auto ios-momentum-scroll divide-y divide-gray-100">
          {results.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                role="option"
                className="w-full text-left px-3 py-2.5 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                onClick={() => onSelectBooking(row.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {row.title || `Booking #${row.booking_number ?? '—'}`}
                  </span>
                  <span
                    className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded capitalize ${getBookingStatusBadgeClass(row.status)}`}
                  >
                    {row.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5 truncate">
                  #{row.booking_number ?? '—'}
                  {row.invoice_number ? ` · Inv ${row.invoice_number}` : ''} · {row.customer_name}
                </p>
                {(row.customer_email || row.customer_phone) && (
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                    {[row.customer_email, row.customer_phone].filter(Boolean).join(' · ')}
                  </p>
                )}
                {row.vehicle_labels.length > 0 ? (
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                    {row.vehicle_labels.join(', ')}
                  </p>
                ) : null}
                {row.start_date ? (
                  <p className="text-[11px] text-gray-400 mt-1">{formatBookingDate(row)}</p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
